/**
 * The device mirror of the recents log, moved from the shell's `platform/recents-store.ts`
 * (kernel apps, WS-14 section 2; layered shell surfaces, R-7).
 *
 * The shape and the pure log algebra live in `@zion/sdk-contracts` (`surfaces/recents-log.ts`),
 * the one home both sides import. This module holds only the two functions that touch
 * `localStorage`, which the contracts package leaves to the host. Both never throw: storage refuses
 * in private mode and at quota, a read failure reads as "no recents" and a write failure reports
 * `false`. Recording is best-effort by design.
 */
import { EMPTY_RECENTS_LOG, parseRecentsLog, type RecentsLog } from '@zion/sdk-contracts';
import { STORAGE_KEYS } from './storage-keys.js';

/**
 * Read the mirror at `STORAGE_KEYS.recents`.
 *
 * @returns The stored log through the total sanitizer `parseRecentsLog`, or `EMPTY_RECENTS_LOG`
 *   when the key is absent, when storage refuses, and when the stored value is not JSON. Never
 *   throws.
 */
export function readRecentsLog(): RecentsLog {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEYS.recents);
  } catch {
    return EMPTY_RECENTS_LOG;
  }
  if (raw === null) return EMPTY_RECENTS_LOG;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_RECENTS_LOG;
  }
  return parseRecentsLog(parsed);
}

/**
 * Write the mirror. The stored form is the wrapper object `{ log }`, so the mirror and the
 * `home-recents` preferences value are byte-identical shapes.
 *
 * @returns `true` when storage took the value, `false` when it refused (quota, private mode). It
 *   writes no other key. Never throws.
 */
export function writeRecentsLog(log: RecentsLog): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.recents, JSON.stringify({ log: log.log }));
    return true;
  } catch {
    return false;
  }
}
